import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Bell, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { getPickems, type Pickem } from '@/api/pickems/getPickems';
import { LiveScoringQuestionsList } from '@/components/live-scoring/LiveScoringQuestionsList';

export const Route = createFileRoute('/dashboard/live-scoring/$pickemId')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as 'scoring-mode' | 'leaderboard' | 'scoring') || 'scoring-mode',
    };
  },
  // @ts-expect-error Inherited from main PR #476 — see comment in
  // routes/dashboard/pickems/$id.tsx for the same loader type-duplication
  // issue around Pickem.style_config.
  loader: async ({ params }) => {
    const { pickems } = await getPickems();
    const pickem = pickems.find(p => p.pickem_id === params.pickemId);
    return { pickem };
  },
  component: LiveScoringModePage,
});

function LiveScoringModePage() {
  const { pickem } = Route.useLoaderData() as { pickem: Pickem | undefined };
  const { pickemId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'scoring-mode' | 'leaderboard' | 'scoring'>(
    search.tab || 'scoring-mode'
  );

  const pickemTitle = pickem?.title_en || pickem?.title_fr || 'Untitled Pickem';

  const handleCancel = () => {
    navigate({ to: '/dashboard/live-scoring' });
  };

  const handleSave = () => {
    // TODO: Implement save functionality
  };

  const handleLeaderboardTab = () => {
    if (pickem?.pickem_id) {
      navigate({
        to: '/dashboard/pickems/$id',
        params: { id: pickem.pickem_id },
        search: { tenant_id: undefined, tab: 'leaderboard' },
      });
    }
  };

  if (!pickem) {
    return (
      <PageContainer>
        <Breadcrumbs overrides={{ 'live-scoring': 'Live Scoring' }} />
        <ContentCard>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">Pickem not found</p>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ 'live-scoring': 'Live Scoring' }} />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-5 w-5 text-gray-600" />
          <div>
            <h1 className="text-2xl font-semibold">Live Scoring</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{pickemTitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel}>
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as typeof activeTab)}>
        <div className="mb-4">
          <TabsList>
            <TabsTrigger value="scoring-mode">Scoring Mode</TabsTrigger>
            <TabsTrigger value="leaderboard" onClick={handleLeaderboardTab}>
              Leaderboard
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="scoring-mode" className="mt-0">
          <ContentCard>
            <LiveScoringQuestionsList pickemId={pickemId} />
          </ContentCard>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
